import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft } from 'react-feather';
import './LegalPages.css';

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page-container">
      <div className="legal-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <button className="home-button" onClick={() => navigate('/')}>
          <Home size={20} />
          <span>Home</span>
        </button>
      </div>

      <div className="legal-page-content">
        <div className="legal-page-title">
          <h1>Terms of Service</h1>
          <p className="last-updated">Last Updated: January 2025</p>
        </div>

        <div className="legal-section">
          <h2>1. Introduction and Acceptance</h2>
          <p>
            Welcome to Brainy AI ("we," "our," or "us"). These Terms of Service ("Terms") govern your
            access to and use of our AI-powered educational platform, including our website, mobile
            applications, and related services (collectively, the "Service").
          </p>
          <p>
            By creating an account or using our Service, you agree to be bound by these Terms and our
            Privacy Policy. If you do not agree to these Terms, please do not use our Service.
          </p>
        </div>

        <div className="legal-section">
          <h2>2. Eligibility</h2>
          <p>
            You must be at least 13 years old to use our Service. If you are between 13 and 18 years
            old (or the age of majority in your jurisdiction), you may only use the Service under the
            supervision of a parent or legal guardian who agrees to be bound by these Terms.
          </p>
          <p>
            By using the Service, you represent and warrant that you meet these eligibility requirements.
          </p>
        </div>

        <div className="legal-section">
          <h2>3. Account Registration and Security</h2>
          <h3>Account Creation</h3>
          <p>
            To access certain features of our Service, you must create an account. You agree to:
          </p>
          <ul>
            <li>Provide accurate, current, and complete information during registration</li>
            <li>Maintain and promptly update your account information</li>
            <li>Maintain the security of your password and account</li>
            <li>Accept all responsibility for activities that occur under your account</li>
            <li>Notify us immediately of any unauthorized access or security breach</li>
          </ul>
          
          <h3>Account Responsibilities</h3>
          <p>
            You are solely responsible for all activity that occurs on your account. We reserve the
            right to suspend or terminate accounts that violate these Terms or engage in fraudulent,
            abusive, or illegal activity.
          </p>
        </div>

        <div className="legal-section">
          <h2>4. Subscription Plans and Billing</h2>
          <h3>Free and Paid Plans</h3>
          <p>
            Brainy AI offers both free and paid subscription plans. The features and limitations of
            each plan are described on our website and may change from time to time.
          </p>
          
          <h3>Billing and Payments</h3>
          <ul>
            <li>
              <strong>Subscription Fees:</strong> Paid plans are billed on a recurring basis (monthly
              or annually) based on your selected plan
            </li>
            <li>
              <strong>Payment Processing:</strong> All payments are processed securely through
              third-party payment processors. We do not store your complete credit card information
            </li>
            <li>
              <strong>Auto-Renewal:</strong> Your subscription will automatically renew at the end of
              each billing period unless you cancel before the renewal date
            </li>
            <li>
              <strong>Price Changes:</strong> We may change subscription prices with 30 days' notice.
              Changes will apply to subsequent billing periods
            </li>
          </ul>

          <h3>Cancellation and Refunds</h3>
          <p>
            You may cancel your subscription at any time through your account settings. Upon cancellation:
          </p>
          <ul>
            <li>You will retain access to paid features until the end of your current billing period</li>
            <li>Your account will automatically revert to the free plan at the end of the billing period</li>
            <li>Refunds are generally not provided for partial subscription periods, except as required by law</li>
            <li>We reserve the right to offer refunds on a case-by-case basis at our sole discretion</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>5. Use of Service and Content</h2>
          <h3>License to Use</h3>
          <p>
            Subject to your compliance with these Terms, we grant you a limited, non-exclusive,
            non-transferable, revocable license to access and use the Service for your personal,
            non-commercial educational purposes.
          </p>

          <h3>Prohibited Uses</h3>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any illegal purpose or in violation of any laws</li>
            <li>Violate the intellectual property rights of others</li>
            <li>Distribute viruses, malware, or other harmful code</li>
            <li>Attempt to gain unauthorized access to our systems or networks</li>
            <li>Scrape, crawl, or use automated tools to collect data from the Service</li>
            <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
            <li>Share your account credentials with others</li>
            <li>Use the Service to create competing products or services</li>
            <li>Harass, abuse, or harm other users</li>
            <li>Upload or transmit any content that is unlawful, harmful, or objectionable</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>6. User Content</h2>
          <h3>Your Content</h3>
          <p>
            You retain ownership of all content you create, upload, or submit to the Service ("User Content").
            By submitting User Content, you grant us a worldwide, non-exclusive, royalty-free license to:
          </p>
          <ul>
            <li>Use, reproduce, and store your User Content to provide and improve the Service</li>
            <li>Create derivative works for the purpose of training our AI models (in anonymized form)</li>
            <li>Display your User Content back to you and as you direct</li>
          </ul>

          <h3>Content Standards</h3>
          <p>
            You are solely responsible for your User Content. You represent and warrant that:
          </p>
          <ul>
            <li>You own or have the necessary rights to submit your User Content</li>
            <li>Your User Content does not violate any third-party rights</li>
            <li>Your User Content complies with these Terms and applicable laws</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>7. AI-Generated Content</h2>
          <h3>Nature of AI Content</h3>
          <p>
            Our Service uses artificial intelligence to generate educational content, including lessons,
            assessments, and learning recommendations. You acknowledge and agree that:
          </p>
          <ul>
            <li>AI-generated content may contain errors, inaccuracies, or incomplete information</li>
            <li>AI responses should not be considered professional advice</li>
            <li>You should verify important information through additional sources</li>
            <li>We do not guarantee the accuracy, completeness, or usefulness of AI-generated content</li>
          </ul>

          <h3>Ownership of AI Content</h3>
          <p>
            AI-generated content created specifically for you remains your property. However, we retain
            the right to use such content in anonymized form for training and improving our AI models.
          </p>
        </div>

        <div className="legal-section">
          <h2>8. Intellectual Property Rights</h2>
          <h3>Our Rights</h3>
          <p>
            The Service and all materials therein, including software, text, graphics, logos, icons,
            images, audio, video, and AI models (collectively, "Platform Content"), are owned by or
            licensed to Brainy AI and are protected by intellectual property laws.
          </p>

          <h3>Trademarks</h3>
          <p>
            "Brainy AI" and our logo are trademarks of our company. You may not use our trademarks
            without our prior written permission.
          </p>
        </div>

        <div className="legal-section">
          <h2>9. Privacy and Data Protection</h2>
          <p>
            Your privacy is important to us. Our Privacy Policy explains how we collect, use, and
            protect your personal information. By using the Service, you consent to our data practices
            as described in our Privacy Policy.
          </p>
          <p>
            We comply with applicable data protection laws, including GDPR and CCPA, where applicable.
          </p>
        </div>

        <div className="legal-section">
          <h2>10. Third-Party Services</h2>
          <p>
            Our Service may contain links to third-party websites or integrate with third-party services.
            We are not responsible for the content, privacy practices, or terms of service of any third
            parties. Your use of third-party services is at your own risk.
          </p>
        </div>

        <div className="legal-section">
          <h2>11. Disclaimers and Limitations of Liability</h2>
          <h3>Service "As Is"</h3>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
            EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR
            A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>

          <h3>No Guarantee of Results</h3>
          <p>
            We do not guarantee that the Service will meet your specific educational goals or produce
            particular learning outcomes. Individual results may vary.
          </p>

          <h3>Limitation of Liability</h3>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, BRAINY AI SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES,
            WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER
            INTANGIBLE LOSSES RESULTING FROM:
          </p>
          <ul>
            <li>Your use or inability to use the Service</li>
            <li>Any unauthorized access to or use of our servers and/or personal information</li>
            <li>Any interruption or cessation of transmission to or from the Service</li>
            <li>Any bugs, viruses, or other harmful code transmitted through the Service</li>
            <li>Any errors or omissions in content or any loss or damage incurred as a result of content</li>
          </ul>

          <h3>Aggregate Liability Cap</h3>
          <p>
            OUR TOTAL AGGREGATE LIABILITY SHALL NOT EXCEED THE GREATER OF (A) $100 OR (B) THE AMOUNT
            YOU PAID TO US IN THE TWELVE MONTHS PRECEDING THE CLAIM.
          </p>
        </div>

        <div className="legal-section">
          <h2>12. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Brainy AI, its affiliates, and their
            respective officers, directors, employees, and agents from any claims, liabilities, damages,
            losses, costs, or expenses (including reasonable attorneys' fees) arising out of or related to:
          </p>
          <ul>
            <li>Your violation of these Terms</li>
            <li>Your use of the Service</li>
            <li>Your User Content</li>
            <li>Your violation of any rights of another party</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>13. Term and Termination</h2>
          <h3>Term</h3>
          <p>
            These Terms remain in effect while you use the Service.
          </p>

          <h3>Termination by You</h3>
          <p>
            You may stop using the Service and delete your account at any time through your account settings.
          </p>

          <h3>Termination by Us</h3>
          <p>
            We reserve the right to suspend or terminate your access to the Service at any time, with or
            without cause or notice, for any reason, including if we reasonably believe you have violated
            these Terms.
          </p>

          <h3>Effect of Termination</h3>
          <p>
            Upon termination:
          </p>
          <ul>
            <li>Your right to use the Service will immediately cease</li>
            <li>We may delete your account and User Content</li>
            <li>You will no longer have access to your learning progress and data</li>
            <li>Provisions that by their nature should survive termination shall survive</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>14. Modifications to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify you of material
            changes by:
          </p>
          <ul>
            <li>Posting the updated Terms on the Service with a new "Last Updated" date</li>
            <li>Sending you an email notification (if you have provided an email address)</li>
            <li>Displaying a notice on the Service</li>
          </ul>
          <p>
            Your continued use of the Service after such modifications constitutes your acceptance of
            the updated Terms. If you do not agree to the modified Terms, you must stop using the Service.
          </p>
        </div>

        <div className="legal-section">
          <h2>15. Dispute Resolution</h2>
          <h3>Informal Resolution</h3>
          <p>
            If you have any concerns or disputes, please contact us first at brainyai.info@gmail.com to
            attempt to resolve the issue informally.
          </p>

          <h3>Arbitration</h3>
          <p>
            Any disputes arising out of or relating to these Terms or the Service shall be resolved
            through binding arbitration in accordance with the rules of the American Arbitration
            Association, except where prohibited by law.
          </p>

          <h3>Class Action Waiver</h3>
          <p>
            You agree that any dispute resolution proceedings will be conducted only on an individual
            basis and not in a class, consolidated, or representative action.
          </p>
        </div>

        <div className="legal-section">
          <h2>16. Governing Law and Jurisdiction</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the United
            Kingdom, without regard to its conflict of law provisions. You agree to submit to the
            exclusive jurisdiction of the courts located in the United Kingdom for any disputes.
          </p>
        </div>

        <div className="legal-section">
          <h2>17. General Provisions</h2>
          <h3>Entire Agreement</h3>
          <p>
            These Terms, together with our Privacy Policy, constitute the entire agreement between you
            and Brainy AI regarding the Service.
          </p>

          <h3>Severability</h3>
          <p>
            If any provision of these Terms is found to be unenforceable, the remaining provisions will
            continue in full force and effect.
          </p>

          <h3>Waiver</h3>
          <p>
            Our failure to enforce any right or provision of these Terms will not be considered a waiver
            of those rights.
          </p>

          <h3>Assignment</h3>
          <p>
            You may not assign or transfer these Terms or your rights hereunder without our prior written
            consent. We may assign our rights and obligations without restriction.
          </p>

          <h3>Force Majeure</h3>
          <p>
            We shall not be liable for any failure or delay in performing our obligations due to causes
            beyond our reasonable control.
          </p>
        </div>

        <div className="legal-section">
          <h2>18. Contact Information</h2>
          <p>
            If you have questions about these Terms of Service, please contact us at:
          </p>
          <div className="contact-info">
            <p>
              <strong>Email:</strong> brainyai.info@gmail.com<br />
              <strong>Website:</strong> www.thebrainyai.com
            </p>
          </div>
        </div>

        <div className="legal-acknowledgment">
          <p>
            By using Brainy AI, you acknowledge that you have read, understood, and agree to be bound
            by these Terms of Service.
          </p>
        </div>
      </div>

      <div className="legal-page-footer">
        <p>© 2025 Brainy AI. All rights reserved.</p>
        <div className="footer-links">
          <button onClick={() => navigate('/terms')}>Terms of Service</button>
          <span>•</span>
          <button onClick={() => navigate('/privacy')}>Privacy Policy</button>
          <span>•</span>
          <button onClick={() => navigate('/')}>Home</button>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;

